import { lazy, type ComponentType } from "react";

type Mod<P> = { default: ComponentType<P> };
type Importer<P> = () => Promise<Mod<P>>;

const RELOAD_KEY = "chunk-reload-attempt";

/**
 * lazy() con retry.
 *
 * In una WebView (app iOS/Android) la fetch di un chunk JS può fallire per un
 * semplice blip di rete: senza retry la pagina resta bianca — è il "a volte si
 * bugga". Qui riproviamo una volta a breve distanza; se anche il secondo
 * tentativo fallisce (caso tipico: chunk non più sul server dopo un deploy)
 * ricarichiamo la pagina una sola volta, con un guard per non entrare in loop.
 */
export function lazyWithRetry<P>(importer: Importer<P>) {
  return lazy(async () => {
    try {
      const mod = await importer();
      sessionStorage.removeItem(RELOAD_KEY);
      return mod;
    } catch {
      await new Promise((r) => setTimeout(r, 400));
      try {
        const mod = await importer();
        sessionStorage.removeItem(RELOAD_KEY);
        return mod;
      } catch (err) {
        if (!sessionStorage.getItem(RELOAD_KEY)) {
          sessionStorage.setItem(RELOAD_KEY, "1");
          window.location.reload();
          // Non renderizzare nulla nell'istante prima che il reload prenda piede.
          return new Promise<Mod<P>>(() => {});
        }
        throw err;
      }
    }
  });
}
